import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  DataGrid,
  Divider,
  IdentityCell,
  Stack,
  TextField,
  TintedStatusPill,
  Typography,
} from '../../../ui/core';
import { hairline, softFill } from '../../../ui/styles';
import { CheckCircleIcon, PersonIcon, SearchIcon } from '../../../ui/icons';
import { useFormatters } from '../../../hooks';
import api from '../../../lib/api';

// ── Summary ────────────────────────────────────────────────────────────────
// The two numbers the Usage tab leads with, in the order they read: a promo is
// first "applied" (added to an account) and only then "redeemed" (actually used
// on a ride), so redeemed is always a subset of applied.
const SUMMARY_ROWS = [
  { key: 'appliedCount', label: 'Applied', caption: 'Code added to a user’s account', Icon: PersonIcon, palette: 'text.secondary' },
  { key: 'redeemedCount', label: 'Redeemed', caption: 'Actually used on a ride', Icon: CheckCircleIcon, palette: 'success.main' },
];

const SummaryRow = ({ row, value, last }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      px: '18px',
      py: '16px',
      borderBottom: (theme) => (last ? 'none' : `1px solid ${hairline(theme)}`),
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 34,
        height: 34,
        borderRadius: '9px',
        backgroundColor: softFill,
        flexShrink: 0,
      }}
    >
      <row.Icon sx={{ fontSize: 18, color: row.palette }} />
    </Box>
    <Stack spacing="1px" sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{row.label}</Typography>
      <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>{row.caption}</Typography>
    </Stack>
    <Typography sx={{ fontSize: '22px', fontWeight: 600, letterSpacing: '-0.01em', color: row.palette }}>
      {value.toLocaleString()}
    </Typography>
  </Box>
);

const ACCOUNTS_PAGE_SIZE = 10;

// Pill search field, matching the promo list rail's search.
const searchFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '999px',
    fontSize: '14px',
    backgroundColor: softFill,
    fieldset: { border: 'none' },
  },
  '& input::placeholder': { color: 'text.inactive', opacity: 1, fontWeight: 400 },
};

// Usage tab body: a summary of applied/redeemed counts, then — below a hairline
// — the actual accounts the promo is attached to, searchable and paginated.
const PromotionUsage = ({ promo }) => {
  const history = useHistory();
  const f = useFormatters();

  // ── Summary state ──────────────────────────────────────────────────────
  const seedSummary = () => ({
    appliedCount: typeof promo?.appliedCount === 'number' ? promo.appliedCount : null,
    redeemedCount: typeof promo?.redeemedCount === 'number' ? promo.redeemedCount : null,
  });
  const [usage, setUsage] = useState(seedSummary);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);

  // Bumped by the Update button to force a fresh fetch of both the summary and
  // the current accounts page without changing the search or page.
  const [reloadNonce, setReloadNonce] = useState(0);
  // When the data was last successfully fetched, shown next to the Update button.
  const [lastUpdated, setLastUpdated] = useState(null);

  // ── Accounts state ─────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [page, setPage] = useState(0); // 0-indexed (DataGrid)
  const [accounts, setAccounts] = useState([]);
  const [accountsCount, setAccountsCount] = useState(0);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(false);

  // Reset to the promo's seed counts when the selection changes (not on reload,
  // so a manual reload doesn't flash back to possibly-stale list counts).
  useEffect(() => {
    setUsage(seedSummary());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.id]);

  // Fetch the applied/redeemed summary for the selected promo (and on reload).
  useEffect(() => {
    if (!promo?.id) return undefined;
    let cancelled = false;
    setSummaryError(false);
    setSummaryLoading(true);
    api.promotions
      .getPromotionUsage({ id: promo.id })
      .then((res) => {
        if (cancelled) return;
        setUsage(res);
        setLastUpdated(new Date());
      })
      .catch(() => !cancelled && setSummaryError(true))
      .finally(() => !cancelled && setSummaryLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.id, reloadNonce]);

  // Debounce keystrokes into a single search; a new query returns to page 1.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);
  useEffect(() => {
    setPage(0);
  }, [debouncedQuery, promo?.id]);

  // Fetch the accounts page whenever the promo, search, or page changes.
  useEffect(() => {
    if (!promo?.id) return undefined;
    let cancelled = false;
    setAccountsError(false);
    setAccountsLoading(true);
    api.promotions
      .getPromotionAccounts({ id: promo.id, search: debouncedQuery, offset: page * ACCOUNTS_PAGE_SIZE, limit: ACCOUNTS_PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setAccounts(res.data);
        setAccountsCount(res.count);
        setLastUpdated(new Date());
      })
      .catch(() => {
        if (cancelled) return;
        setAccountsError(true);
        setAccounts([]);
        setAccountsCount(0);
      })
      .finally(() => !cancelled && setAccountsLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.id, debouncedQuery, page, reloadNonce]);

  const columns = useMemo(
    () => [
      { field: 'id', hide: true },
      {
        field: 'name',
        headerName: 'Account',
        headerIcon: <PersonIcon />,
        flex: 1,
        minWidth: 240,
        renderCell: ({ row }) => <IdentityCell name={row.name} secondary={row.email} colorKey={row.id} />,
      },
      { field: 'phoneNumber', headerName: 'Phone', minWidth: 150 },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 130,
        renderCell: ({ row }) =>
          row.redeemed ? (
            <TintedStatusPill palette="success" label="Redeemed" />
          ) : (
            <TintedStatusPill label="Applied" />
          ),
      },
    ],
    [],
  );

  const rows = useMemo(
    () =>
      accounts.map((a) => {
        const name = `${a.firstName || ''} ${a.lastName || ''}`.trim();
        const phone = a.phoneNumber || '';
        return {
          id: a.id,
          name: name || a.email || 'Unknown account',
          email: a.email || '',
          phoneNumber: f?.phone?.toInternational ? f.phone.toInternational(phone) : phone,
          redeemed: !!a.redeemed,
        };
      }),
    [accounts, f],
  );

  const hasCounts = typeof usage.appliedCount === 'number';
  const reloading = summaryLoading || accountsLoading;
  const reload = () => setReloadNonce((n) => n + 1);

  const renderSummary = () => {
    if (summaryLoading && !hasCounts) {
      return (
        <Box display="flex" justifyContent="center" p="24px">
          <CircularProgress size={20} />
        </Box>
      );
    }
    if (summaryError && !hasCounts) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
          Usage totals aren’t available for this promotion right now.
        </Typography>
      );
    }
    return (
      <Box sx={{ maxWidth: 460, borderRadius: '12px', border: (theme) => `1px solid ${hairline(theme)}`, overflow: 'hidden' }}>
        {SUMMARY_ROWS.map((row, i) => (
          <SummaryRow key={row.key} row={row} value={usage[row.key] || 0} last={i === SUMMARY_ROWS.length - 1} />
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {/* Update refetches both the summary and the current accounts page (search
          and page are preserved) so the tab reflects the latest live data. */}
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap="12px" mb="16px">
        {lastUpdated && (
          <Typography sx={{ fontSize: '12px', color: 'text.inactive' }}>
            Updated {lastUpdated.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          loading={reloading}
          onClick={reload}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600,
            borderColor: (theme) => hairline(theme),
            color: 'text.primary',
            '&:hover': { borderColor: (theme) => hairline(theme), backgroundColor: softFill },
          }}
        >
          Update
        </Button>
      </Box>

      {renderSummary()}

      <Divider sx={{ my: '24px', borderColor: (theme) => hairline(theme) }} />

      {/* Accounts this promo is attached to */}
      <Box display="flex" alignItems="center" justifyContent="space-between" gap="12px" mb="12px" flexWrap="wrap">
        <Stack spacing="1px">
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Accounts</Typography>
          <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
            {accountsError ? 'Applied to —' : `Applied to ${accountsCount.toLocaleString()} account${accountsCount === 1 ? '' : 's'}`}
          </Typography>
        </Stack>
        <TextField
          size="small"
          placeholder="Search name, email, phone"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 17, mr: '6px', color: 'text.secondary' }} /> }}
          sx={{ width: { xs: '100%', sm: 280 }, ...searchFieldSx }}
        />
      </Box>

      {accountsError ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', py: '8px' }}>
          The accounts list isn’t available for this promotion right now.
        </Typography>
      ) : !accountsLoading && rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', py: '8px' }}>
          {debouncedQuery ? 'No accounts match your search.' : 'This promo hasn’t been added to any accounts yet.'}
        </Typography>
      ) : (
        <Box sx={{ height: 440, borderRadius: '12px', border: (theme) => `1px solid ${hairline(theme)}`, overflow: 'hidden' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={accountsLoading}
            paginationMode="server"
            rowCount={accountsCount}
            page={page}
            pageSize={ACCOUNTS_PAGE_SIZE}
            onPageChange={(p) => setPage(p)}
            onRowClick={({ row }) => history.push(`/users/${row.id}`)}
          />
        </Box>
      )}
    </Box>
  );
};

export default PromotionUsage;
